import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartBar } from 'react-icons/fa';
import { Button, DateRangeFilter, getDefaultDateRange } from '../UI';
import useGraphMetrics, { alignSeries } from '../../Routes/Visualization/graphData';
import { formatCurrency, formatNumber } from '../../Routes/Visualization/chartTheme';
import { datasetsOf } from '../../Routes/Visualization/graphCards';
import { useAllowedSections } from '../../Routes/customHooks';

const BREAKDOWNS = [
  { key: 'diagnosis.primary', title: 'Primary Diagnosis', column: 'Diagnosis' },
  { key: 'diagnosis.secondary', title: 'Secondary Diagnosis', column: 'Diagnosis' },
  { key: 'diagnosis.tertiary', title: 'Tertiary Diagnosis', column: 'Diagnosis' },
  // "Status of Patient" (`patientStatus`) is an alias of this metric - same
  // fields, same list - so it is not shown as a second table.
  { key: 'statusByVisit', title: 'Status As Per Visit', column: 'Status' },
  { key: 'statusByTreatment', title: 'Status As Per Treatment', column: 'Status' },
  { key: 'patientReference', title: 'Referred by relation', column: 'Relation' },
  { key: 'appointment.channel', title: 'Appointment Breakdown by Channel', column: 'Channel' },
];

const FEES_SOURCES = ['trend.fees', 'trend.prescriptions'];
// Monthly fees split by payment mode: `{ keys, labels, datasets: [{ label: mode,
// data }] }`. Read defensively - if it is missing or fails, the table falls back
// to the plain monthly total. `trend.fees` carries datasets too, but split by
// Payment Details row rather than mode, so it is not used here.
const FEES_BY_MODE_SOURCES = ['trend.feesByMode'];
const METRIC_KEYS = [...BREAKDOWNS.map((card) => card.key), ...FEES_SOURCES, ...FEES_BY_MODE_SOURCES];
// Rows shown before the "Show all" toggle. Only non-zero rows count.
const MAX_ROWS = 8;

const HEAD_CLASS =
  'bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100';

const Card = ({ title, className = '', children }) => (
  <div className={`bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm ${className}`}>
    <h3 className="text-base font-bold text-slate-800 mb-3 tracking-tight">{title}</h3>
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const Placeholder = ({ children }) => (
  <p className="px-3 py-8 text-center text-xs text-slate-400">{children}</p>
);

const BreakdownTable = ({ column, metric, loading, reason }) => {
  const [expanded, setExpanded] = useState(false);
  if (loading) return <Placeholder>Loading…</Placeholder>;
  if (reason) return <Placeholder>{reason}</Placeholder>;

  const labels = metric?.labels || [];
  const data = metric?.data || [];
  const total = data.reduce((sum, value) => sum + (Number(value) || 0), 0);
  // Dropdown-backed metrics list every configured option, zeros included, in
  // the configured order (with "Other" where the clinic put it). Zero rows are
  // dropped so options further down the list (e.g. "Lost Recovery") are not
  // hidden behind empty ones; the order is kept, so the rows still sum to Total.
  const allRows = labels
    .map((label, index) => ({ label, count: Number(data[index]) || 0 }))
    .filter((row) => row.count > 0);
  const rows = expanded ? allRows : allRows.slice(0, MAX_ROWS);
  const hidden = allRows.length - rows.length;

  if (!allRows.length) return <Placeholder>Nothing recorded in this range</Placeholder>;

  return (
    <table className="w-full text-left text-xs text-slate-700">
      <thead className={HEAD_CLASS}>
        <tr>
          <th className="px-3 py-2.5">{column}</th>
          <th className="px-3 py-2.5 text-right">Count</th>
          <th className="px-3 py-2.5 text-right">Share</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.label} className="hover:bg-slate-50/70 transition">
            <td className="px-3 py-2.5 font-medium text-slate-800">{row.label}</td>
            <td className="px-3 py-2.5 text-right font-semibold">{formatNumber(row.count)}</td>
            <td className="px-3 py-2.5 text-right text-slate-500">
              {total ? `${Math.round((row.count / total) * 100)}%` : '—'}
            </td>
          </tr>
        ))}
        {allRows.length > MAX_ROWS && (
          <tr>
            <td colSpan={3} className="px-3 py-2 text-center">
              <button
                type="button"
                onClick={() => setExpanded((open) => !open)}
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 transition"
              >
                {expanded ? 'Show fewer' : `Show all (${hidden} more)`}
              </button>
            </td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr className="border-t border-slate-100 bg-slate-50/60">
          <td className="px-3 py-2.5 font-semibold text-slate-600">Total</td>
          <td className="px-3 py-2.5 text-right font-bold text-slate-800">{formatNumber(total)}</td>
          <td className="px-3 py-2.5" />
        </tr>
      </tfoot>
    </table>
  );
};

/**
 * One `{ keys, labels, data }` per payment mode, so the modes line up with the
 * fee and prescription months through alignSeries like any other series.
 */
const modeSeriesOf = (metric) =>
  (datasetsOf(metric) || []).map((set) => ({
    mode: set.label,
    series: { keys: metric?.keys, labels: metric?.labels, data: set.data },
  }));

const FeesTable = ({ fees, prescriptions, byMode, loading, reason }) => {
  if (loading) return <Placeholder>Loading…</Placeholder>;
  if (reason) return <Placeholder>{reason}</Placeholder>;

  const modes = modeSeriesOf(byMode);
  // `trend.fees` is split by Payment Details row but still carries the
  // per-month total in `data`, so the collected column reads that.
  const { labels, series } = alignSeries([
    fees || {},
    prescriptions || {},
    ...modes.map((entry) => entry.series),
  ]);
  const rows = labels.map((label, index) => {
    const byModeValues = modes.map((_, modeIndex) => Number(series[2 + modeIndex]?.[index]) || 0);
    return {
      label,
      collected: Number(series[0]?.[index]) || 0,
      count: Number(series[1]?.[index]) || 0,
      byMode: byModeValues,
    };
  });

  if (!rows.length) return <Placeholder>No prescriptions in this range</Placeholder>;

  const collected = rows.reduce((sum, row) => sum + row.collected, 0);
  const count = rows.reduce((sum, row) => sum + row.count, 0);
  const modeTotals = modes.map((_, modeIndex) => rows.reduce((sum, row) => sum + row.byMode[modeIndex], 0));

  return (
    <table className="w-full text-left text-xs text-slate-700">
      <thead className={HEAD_CLASS}>
        <tr>
          <th className="px-3 py-2.5">Month</th>
          <th className="px-3 py-2.5 text-right">Prescriptions</th>
          {modes.map((entry) => (
            <th key={entry.mode} className="px-3 py-2.5 text-right whitespace-nowrap">
              {entry.mode}
            </th>
          ))}
          <th className="px-3 py-2.5 text-right">{modes.length ? 'Total' : 'Collected'}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.label} className="hover:bg-slate-50/70 transition">
            <td className="px-3 py-2.5 font-medium text-slate-800">{row.label}</td>
            <td className="px-3 py-2.5 text-right">{formatNumber(row.count)}</td>
            {row.byMode.map((value, modeIndex) => (
              <td key={modes[modeIndex].mode} className="px-3 py-2.5 text-right whitespace-nowrap">
                {formatCurrency(value)}
              </td>
            ))}
            <td className="px-3 py-2.5 text-right font-semibold text-emerald-600">
              {formatCurrency(row.collected)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-slate-100 bg-slate-50/60">
          <td className="px-3 py-2.5 font-semibold text-slate-600">Total</td>
          <td className="px-3 py-2.5 text-right font-semibold text-slate-800">
            {formatNumber(count)}
          </td>
          {modeTotals.map((value, modeIndex) => (
            <td key={modes[modeIndex].mode} className="px-3 py-2.5 text-right font-semibold text-slate-800 whitespace-nowrap">
              {formatCurrency(value)}
            </td>
          ))}
          <td className="px-3 py-2.5 text-right font-bold text-emerald-600">
            {formatCurrency(collected)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

const MetricTables = () => {
  const [range, setRange] = useState(getDefaultDateRange());
  const navigate = useNavigate();
  const { metrics, failed, loading, error, status } = useGraphMetrics(range, METRIC_KEYS);

  if (status === 401 || status === 403) return null;

  const reasonFor = (key) => failed?.[key] || (error ? 'Could not load this table' : '');
  const feesByMode =
    FEES_BY_MODE_SOURCES.map((key) => metrics?.[key]).find((metric) => datasetsOf(metric)) || null;

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800 tracking-tight">Clinic Analytics</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Patient, diagnosis and fee breakdowns for the selected range
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter
            startDate={range.startDate}
            endDate={range.endDate}
            onChange={setRange}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/graphs')}
            icon={<FaChartBar className="w-3.5 h-3.5" />}
          >
            Open Graphs
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <Card
          title={feesByMode ? 'Fees Collection Summary by Payment Mode' : 'Fees Collection Summary'}
          className={feesByMode ? 'lg:col-span-2' : ''}
        >
          <FeesTable
            fees={metrics?.['trend.fees']}
            prescriptions={metrics?.['trend.prescriptions']}
            byMode={feesByMode}
            loading={loading}
            reason={FEES_SOURCES.map(reasonFor).find(Boolean)}
          />
        </Card>

        {BREAKDOWNS.map((card) => (
          <Card key={card.key} title={card.title}>
            <BreakdownTable
              column={card.column}
              metric={metrics?.[card.key]}
              loading={loading}
              reason={reasonFor(card.key)}
            />
          </Card>
        ))}
      </div>
    </section>
  );
};

export default function DashboardMetricTables() {
  const allowedSections = useAllowedSections();

  if (!Array.isArray(allowedSections) || !allowedSections.includes('graphs')) return null;

  return <MetricTables />;
}
